import { datadogLogs } from '@datadog/browser-logs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';

import { GiveBoxEvent } from '@bonusly/common-give-box';
import {
	Box,
	Button,
	Center,
	GiftWrapOutline,
	HStack,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { CollectibleInventoryItem, useCurrentUser, User } from '@bonusly/who-am-i';

import { DEFAULT_GIFT_WRAP_USES } from '../../../../config/recognition';
import { getCollectibleImageUrl } from './collectible-gallery-image';

type PostPurchaseCollectibleModalStore = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
};

export const usePostPurchaseCollectibleModal = create<PostPurchaseCollectibleModalStore>((set) => ({
	isOpen: false,
	onOpen: () => set({ isOpen: true }),
	onClose: () => set({ isOpen: false }),
}));

export function getActiveCollectibleItem(user: User): CollectibleInventoryItem | undefined {
	const inventory = user.collectible_inventory;
	if (!Array.isArray(inventory)) {
		// Log only when we received an unexpected value (truthy but not array) - helps debug source
		if (inventory !== undefined && inventory !== null) {
			datadogLogs.logger.warn('[PostPurchaseCollectibleModal] user.collectible_inventory is not an array', {
				userId: user.id,
				collectibleInventoryValue: inventory,
				collectibleInventoryType: typeof inventory,
				collectibleInventoryJson: JSON.stringify(inventory),
			});
		}
		return undefined;
	}
	return inventory.find((item) => item.remaining_uses > 0 && (!item.expires_at || new Date(item.expires_at) > new Date()));
}

export const PostPurchaseCollectibleModal: FC = () => {
	const { t } = useTranslation('rewards', { keyPrefix: 'reward_details.postPurchaseCollectible' });
	const { isOpen, onClose } = usePostPurchaseCollectibleModal();
	const user = useCurrentUser();
	const dispatch = useDispatch();

	const collectibleItem = getActiveCollectibleItem(user);
	const pattern = collectibleItem?.variant_data.pattern ?? 'stripes';
	const color = collectibleItem?.variant_data.color ?? 'teal';
	const imageUrl = getCollectibleImageUrl(pattern, color);

	const handleRecognize = () => {
		onClose();
		dispatch(GiveBoxEvent.Open);
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', sm: 'lg' }}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t('title')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{!collectibleItem ? (
						<Center py={10}>
							<Spinner />
						</Center>
					) : (
						<VStack spacing={3}>
							<Box borderWidth="1px" borderColor="border.default" borderRadius="md" overflow="hidden" h="240px">
								{imageUrl && <Image src={imageUrl} alt={`${pattern} ${color} gift wrap`} w="full" h="full" objectFit="cover" />}
							</Box>

							<HStack spacing={2} w="full">
								<GiftWrapOutline boxSize={8} />
								<Text>{t('remainingCount', { count: collectibleItem.remaining_uses, total: DEFAULT_GIFT_WRAP_USES })}</Text>
							</HStack>
						</VStack>
					)}
				</ModalBody>

				<ModalFooter>
					<Button onClick={handleRecognize} w="full">
						{t('recognizeButton')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
